import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { HashRouter, useLocation } from "react-router-dom";
import { SingletonHooksContainer } from "react-singleton-hook";
import { BaseCSS } from "styled-bootstrap-grid";
import styled, { StyleSheetManager, createGlobalStyle } from "styled-components";
// import { FontTheme } from "react-builder";
import {
  ContainerAttributes,
  InputTheme,
  TranslationProvider,
  devices,
} from "react-builder";
import { Page } from "../attributes/Page";
import { FreshChat } from "../components/FreshChat";
import { ICanChat } from "../components/ICanChat";
import { LoginDialog } from "../components/LoginDialog";
import { AuthContext, AuthProvider } from "react-builder";
import { ConfigsProvider, IframeContext } from "react-builder";
import { DomainConfigContext, DomainConfigProvider, Product } from "react-builder";
import { isEditor } from "react-builder";
import {
  Attribute,
  AttributeGroup,
  AttributeGroupType,
  AttributeProperty,
  DrawerNavProvider,
} from "react-builder";
import { ButtonStateAttributes } from "react-builder";
import { FontTheme } from "react-builder";
import { LoginDialogV2 } from "../components/LoginDialogV2";
import { ProductApiContextProvider } from "../custom-hooks/useProductApi";
import { ProductServerAuth, ProductServerAuthContext } from "../providers/ProductServerAuth";
import { UserDataContextProvider } from "../providers/ElearningV2";

var classNames = require("classnames");

export enum ChatType {
  FreshChat = "FreshChat",
  ICanChat = "ICanChat",
}

export class LinkAttributes extends Attribute {
  @AttributeProperty({ type: "Number", cssProperty: "font-size" })
  size: string = "";
  @AttributeProperty({
    type: "Select",
    selectOptions: ["normal", "bold", "bolder"],
    cssProperty: "font-weight",
  })
  weight: string = "normal";
  @AttributeProperty({ type: "Color", cssProperty: "background" })
  background: string = "transparent";
  @AttributeProperty({ type: "Color", cssProperty: "color" })
  color: string = "auto";
  @AttributeProperty({ type: "Number", cssProperty: "padding" })
  padding: string = "";
  @AttributeProperty({
    type: "Boolean",
    cssProperyBuilder: (val: boolean) =>
      val ? `text-decoration: underline` : "text-decoration: none",
  })
  underline: boolean = false;
}

@AttributeGroup({ blockName: "Link", type: AttributeGroupType.State })
export class LinkTheme extends Attribute {
  @AttributeProperty({ type: "Object", cssTarget: "" })
  static: LinkAttributes = new LinkAttributes();
  @AttributeProperty({ type: "Object", cssTarget: ":hover" })
  hover: LinkAttributes = new LinkAttributes();
  @AttributeProperty({ type: "Object", cssTarget: ".active" })
  active: LinkAttributes = new LinkAttributes();
}

class NavMenuAttributes extends Attribute {
  @AttributeProperty({ type: "String", cssProperty: "box-shadow" })
  shadow: string = "auto";

  @AttributeProperty({
    type: "Boolean",
    cssProperyBuilder: (val) => (val ? `position: sticky;` : ""),
  })
  sticky: boolean = true;

  @AttributeProperty({ type: "Color", cssProperty: "background-color" })
  background: string = "";

  @AttributeProperty({ type: "Image" })
  logo?: string;
}

@AttributeGroup({ type: AttributeGroupType.State })
class Typography extends Attribute {
  @AttributeProperty({ type: "Object", cssTarget: " h1" })
  h1: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " h2" })
  h2: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " h3" })
  h3: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " h4" })
  h4: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " h5" })
  h5: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " p" })
  p: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " label" })
  label: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " mark" })
  mark: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " mark2" })
  mark2: FontTheme = new FontTheme();
  @AttributeProperty({ type: "Object", cssTarget: " b" })
  bold: FontTheme = new FontTheme();
}

export class ThemeColorAttributes extends Attribute {
  @AttributeProperty({ type: "Color", input: "color" })
  primary?: string = "purple";
  @AttributeProperty({ type: "Color", input: "color" })
  secondary?: string;
  @AttributeProperty({ type: "Color", input: "color" })
  dark: string = "#000000";
  @AttributeProperty({ type: "Color", input: "color" })
  light: string = "#ffffff";
  @AttributeProperty({ type: "Color", input: "color" })
  success: string = "#00d390";
  @AttributeProperty({ type: "Color", input: "color" })
  danger: string = "#ff3131";
  @AttributeProperty({ type: "Color", input: "color" })
  warning: string = "#ffd231";
  @AttributeProperty({ type: "Color", input: "color" })
  info: string = "#318aff";
}

class Theme extends Attribute {
  @AttributeProperty({
    type: "String",
    selectOptions: ["Roboto", "Poppins", "Permanent Marker", "Open Sans", "Raleway", "Montserrat"],
    cssProperty: "font-family",
  })
  fontFamily: string = "Roboto";

  @AttributeProperty({ type: "Object" })
  colors: ThemeColorAttributes = new ThemeColorAttributes();

  // @AttributeProperty({ type: "Object", cssTarget: " nav" })
  // menu: NavMenuAttributes = new NavMenuAttributes();

  @AttributeProperty({ type: "Object", cssTarget: " button" })
  button: ButtonStateAttributes = new ButtonStateAttributes();

  @AttributeProperty({ title: 'Elevated', type: "Object", cssTarget: " button.elevated, a[role=button].elevated" })
  elevatedButton: ButtonStateAttributes = new ButtonStateAttributes();
  @AttributeProperty({ title: 'Elevated', type: "Object", cssTarget: " button.filled, a[role=button].filled" })
  filledButton: ButtonStateAttributes = new ButtonStateAttributes();
  @AttributeProperty({ title: 'Elevated', type: "Object", cssTarget: " button.outlined, a[role=button].outlined" })
  outlinedButton: ButtonStateAttributes = new ButtonStateAttributes();
  @AttributeProperty({ title: 'Elevated', type: "Object", cssTarget: " button.text, a[role=button].text" })
  textButton: ButtonStateAttributes = new ButtonStateAttributes();

  // @AttributeProperty({ type: 'Object', cssTarget: ' .navbar li a[role=button]' })
  // navButton: ButtonStateAttributes = new ButtonStateAttributes()

  // @AttributeProperty({ type: 'Object', cssTarget: ' .navbar a.nav-link' })
  // navLink: LinkTheme = new LinkTheme();

  @AttributeProperty({ type: "Object", cssTarget: "" })
  typography: Typography = new Typography();

  @AttributeProperty({ type: "Object" })
  mobileTypography: Typography = new Typography();

  @AttributeProperty({ type: "Object", cssTarget: " a:not(.nav-link):not([role=button])" })
  link: LinkTheme = new LinkTheme();

  @AttributeProperty({ type: "Object", cssTarget: " .form-group" })
  input: InputTheme = new InputTheme();

  @AttributeProperty({ type: "Object", title: "Login Form" })
  loginForm: ContainerAttributes = new ContainerAttributes();
}

export class ProductHomepageAttributes extends Attribute {
  @AttributeProperty({ type: "Object", cssTarget: "" })
  theme = new Theme();

  @AttributeProperty({
    type: AttributeGroupType.List,
    arrayType: Page,
    arrayLabelProperty: "title",
    title: "Pages",
  })
  pages: Page[] = [];

  @AttributeProperty({ type: "String" })
  signupButtonText: string = "Free Sign Up";

  @AttributeProperty({
    type: "Select",
    selectOptions: [Product.Vod, Product.Iq, Product.Elearning],
  })
  product: Product = Product.Vod;

  // @AttributeProperty({ type: "String" })
  // site: string = "";

  @AttributeProperty({ type: "Select", selectOptions: [ChatType.FreshChat, ChatType.ICanChat] })
  chat: ChatType = ChatType.FreshChat;

  @AttributeProperty({ type: "Boolean" })
  displayRefund: boolean = true;
}

export const HomepageContext = createContext<ProductHomepageAttributes>(
  new ProductHomepageAttributes()
);

export function ProductHomepageV2(props: {
  attributes: ProductHomepageAttributes;
  children: JSX.Element;
}) {
  const date = new Date();

  const site = (window as any).pbSite || `${window.location.protocol}//app.${window.location.hostname.split(".").slice(-2).join(".")}`;

  const query = new URLSearchParams(window.location.search);

  return (
    <ConfigsProvider configJson={{
      date: { year: date.getFullYear() },
      query: {
        all: query.toString(),
      }
    }}>
      <DomainConfigProvider product={props.attributes.product}>
        <ProductServerAuth site={site}>
          <ProductApiContextProvider site={site}>
            <UserDataContextProvider>
              <HomepageContext.Provider value={props.attributes}>
                <SingletonHooksContainer />
                <HashRouter>
                  <DrawerNavProvider>
                    <TranslationProvider>
                      <Homepage attributes={props.attributes}>{props.children}</Homepage>
                    </TranslationProvider>
                  </DrawerNavProvider>
                </HashRouter>
              </HomepageContext.Provider>
            </UserDataContextProvider>
          </ProductApiContextProvider>
        </ProductServerAuth>
      </DomainConfigProvider>
    </ConfigsProvider>
  );
}

const mobileViewportSize = "790px";

const HompageTheme = styled.div(
  (props: { attributes: ProductHomepageAttributes }) => `

    & *{
        font-family: "${props.attributes.theme.fontFamily}", sans-serif !important;
    }

    & {

        a:-webkit-any-link {
            text-decoration: none;
        }

        #touch-menu {
            min-width: 20px;
            width: 20px;
            height: 30px;
            position: relative;
            margin-right: 20px;
        }

        #touch-menu span {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: #666;
            width: 100%;
            height: 2px;
            transition: .2s ease;
        }

        #touch-menu span:before, #touch-menu span:after {
            position: absolute;
            width: 100%;
            height: 2px;
            content: '';
            background: #666;
            transition: .2s ease;
        }

        #touch-menu span:before {
            top: -5px;
        }

        #touch-menu span:after {
            top: 5px;
        }

        #touch-menu.on span {
            background: transparent;
            height: 0;
        }

        #touch-menu.on span:before {
            top: -10px;
            transform: rotate(45deg) translate(6px, 5px);
            background: rgba(34, 34, 34, 0.5);
        }

        #touch-menu.on span:after {
            transform: rotate(-45deg) translate(5px, -5px);
            background: rgba(34, 34, 34, 0.5);
        }

        @media(min-width: ${mobileViewportSize}){

            .d-mobile{
                display: none !important;
            }
        }
        @media(max-width: ${mobileViewportSize}){

            
            .d-desktop{
                display: none !important;
            }
        }
        nav {
            

        }
        nav {
        
        }
        /* .navbar {
            display: flex;
            align-items: stretch;
            width: 100%;
            margin: 0;
            padding: 0;
        }

         nav > .container{
            height: 100%;
        }
        .container {
            display: flex;
            align-items: stretch;
            -webkit-box-pack: justify;
            -ms-flex-pack: justify;
            width: calc(100% - 20px);
            
            max-width: 100%;
            margin: auto;
            padding: 0 10px;
            transition: 0.2s ease;
        } 

        .logo {
            margin-right: 30px;
        }
        .logo {
            cursor: pointer;
        }
        .logo {
            position: relative;
            cursor: pointer;
            width: 160px;
            margin-right: 30px;
        }
        .logo {
            width: 160px;
        }

        .navbar {
        
            li{
                display: flex;
                align-items: center;
                position: relative;

                a {
                    position: relative;
                    display: -webkit-box;
                    display: -moz-box;
                    display: -ms-flexbox;
                    display: -webkit-flex;
                    display: flex;
                    -webkit-box-align: center;
                    -moz-box-align: center;
                    -ms-flex-align: center;
                    -webkit-align-items: center;
                    align-items: center;
                    margin: 0 4px;
                    transition: 0.2s ease;
                    text-align: center;
                    white-space: nowrap;
                    cursor: pointer;
                }

                .sub-menu{
                    display:none;
                    position: absolute;
                    top: 100%;
                    left: -10px;
                    padding: 10px;
                }

                &:hover .sub-menu{
                    display: block;
                }
            }

        }

        .navbar ul:last-child {
            margin-left: auto;
        }
        .navbar ul {
            display: flex;
            align-items: stretch;
            margin: 0;
        }

        

        .page-nav {
            a {
                margin-right: 10px;
            }
        }
        */
        p,
        li {
            font-size: 14px;
        }

        font-family: "Poppins", sans-serif;

        @media (min-width: ${mobileViewportSize}) {
            /* .navbar {
                border-radius: 4px;
            }
            .navbar {
                position: relative;
                min-height: 50px;
                border: 1px solid transparent;
            }*/
        }
    
        input[type="text"],
        input[type="password"],
        input[type="email"],
        input[type="tel"],
        select,
        textarea {
            outline: 0;
            transition: 0.3s ease;
        }

        select{
            appearance: auto;
            background-image: none !important;
        }

        .company {
            font-size: 12px;
        }
    
        
    
        

        @media (max-width: ${mobileViewportSize}) {
           /* nav .navbar {
                position: absolute;
                width: 100%;
                height: 100vh;
                margin: 0;
                padding: 20px 10px;
                top: 80px;
                left: 0;
                background: #fbfbff;
                color: #222;
                box-shadow: inset 0 10px 16px -10px rgba(0, 0, 0, 0.16);
            }*/
        }

        

        .Homepage{

            .primary{
                color: ${props.attributes.theme.colors.primary}
            }

        }
        
        ${props.attributes.toCSS()}

        @media (max-width: ${mobileViewportSize}){
            .navbar {
                align-items: start;
                ul {
                    display: block;
                    margin: initial !important;
                    
                    & a{
                        display: block;
                    }
                }
            }

            ${props.attributes.theme.mobileTypography.toCSS()}

        }

        ul.user-nav.d-mobile {
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin: 0;
        }
    }

    
`
);

const buildGlobalStyle = (props: { attributes: ProductHomepageAttributes }) => createGlobalStyle`

    :root {
        --primary: ${props.attributes.theme.colors.primary};
        --secondary: ${props.attributes.theme.colors.secondary};
        --dark: ${props.attributes.theme.colors.dark};
        --light: ${props.attributes.theme.colors.light};
        --danger: ${props.attributes.theme.colors.danger};
        --success: ${props.attributes.theme.colors.success};
        --warning: ${props.attributes.theme.colors.warning};
        --info: ${props.attributes.theme.colors.info};
    }

    body {
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    }

    code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
        monospace;
    }

    figure{
        margin: 0;
    }

    /** base block style **/
    /**
    * Colors
    */
    /**
    * Breakpoints & Media Queries
    */
    /**
    * SCSS Variables.
    *
    * Please use variables from this sheet to ensure consistency across the UI.
    * Don't add to this sheet unless you're pretty sure the value will be reused in many places.
    * For example, don't add rules to this sheet that affect block visuals. It's purely for UI.
    */
    /**
    * Colors
    */
    /**
    * Fonts & basic variables.
    */
    /**
    * Grid System.
    * https://make.wordpress.org/design/2019/10/31/proposal-a-consistent-spacing-system-for-wordpress/
    */
    /**
    * Dimensions.
    */
    /**
    * Shadows.
    */
    /**
    * Editor widths.
    */
    /**
    * Block & Editor UI.
    */
    /**
    * Block paddings.
    */
    /**
    * React Native specific.
    * These variables do not appear to be used anywhere else.
    */
    /**
    *  Converts a hex value into the rgb equivalent.
    *
    * @param {string} hex - the hexadecimal value to convert
    * @return {string} comma separated rgb values
    */
    /**
    * Breakpoint mixins
    */
    /**
    * Long content fade mixin
    *
    * Creates a fading overlay to signify that the content is longer
    * than the space allows.
    */
    /**
    * Focus styles.
    */
    /**
    * Applies editor left position to the selector passed as argument
    */
    /**
    * Styles that are reused verbatim in a few places
    */
    /**
    * Allows users to opt-out of animations via OS-level preferences.
    */
    /**
    * Reset default styles for JavaScript UI based pages.
    * This is a WP-admin agnostic reset
    */
    /**
    * Reset the WP Admin page styles for Gutenberg-like pages.
    */
    .wp-block-columns {
    display: flex;
    margin-bottom: 0;
    box-sizing: border-box;
    flex-wrap: wrap !important;
    align-items: initial !important;
    /**
    * All Columns Alignment
    */
    }

    .wp-block-columns, html :where(.wp-block){
    margin-bottom: 0px;
    }

    @media (min-width: 782px) {
    .wp-block-columns {
        flex-wrap: nowrap !important;
    }
    }
    .wp-block-columns.are-vertically-aligned-top {
    align-items: flex-start;
    }
    .wp-block-columns.are-vertically-aligned-center {
    align-items: center;
    }
    .wp-block-columns.are-vertically-aligned-bottom {
    align-items: flex-end;
    }
    @media (max-width: 781px) {
    .wp-block-columns:not(.is-not-stacked-on-mobile) > .wp-block-column {
        flex-basis: 100% !important;
    }
    }
    @media (min-width: 782px) {
    .wp-block-columns:not(.is-not-stacked-on-mobile) > .wp-block-column {
        flex-basis: 0;
        flex-grow: 1;
    }
    .wp-block-columns:not(.is-not-stacked-on-mobile) > .wp-block-column[style*=flex-basis] {
        flex-grow: 0;
    }
    }
    .wp-block-columns.is-not-stacked-on-mobile {
    flex-wrap: nowrap !important;
    }
    .wp-block-columns.is-not-stacked-on-mobile > .wp-block-column {
    flex-basis: 0;
    flex-grow: 1;
    }
    .wp-block-columns.is-not-stacked-on-mobile > .wp-block-column[style*=flex-basis] {
    flex-grow: 0;
    }

    :where(.wp-block-columns.has-background) {
    padding: 1.25em 2.375em;
    }

    .wp-block-column {
    flex-grow: 1;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: break-word;
    /**
    * Individual Column Alignment
    */
    }
    .wp-block-column.is-vertically-aligned-top {
    align-self: flex-start;
    }
    .wp-block-column.is-vertically-aligned-center {
    align-self: center;
    }
    .wp-block-column.is-vertically-aligned-bottom {
    align-self: flex-end;
    }
    .wp-block-column.is-vertically-aligned-top, .wp-block-column.is-vertically-aligned-center, .wp-block-column.is-vertically-aligned-bottom {
    width: 100%;
    }

    img{
    max-width: 100%;
    }



    /* Block Typography Classes ------------------ */

    .has-text-align-left {

        /*rtl:ignore*/
        text-align: left;
    }

    .has-text-align-center {
        text-align: center;
    }

    .has-text-align-right {
        text-align: right;
    }

    .has-drop-cap:not(:focus)::first-letter {
        color: #cd2653;
        font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, sans-serif;
        font-size: 5.1em;
        font-weight: 800;
        margin: 0.05em 0.1em 0 0;
    }

    .has-drop-cap:not(:focus)::first-letter::after {
        content: "";
        display: table;
        clear: both;
    }

    .has-drop-cap:not(:focus)::after {
        padding: 0;
    }

    body{
    margin: 0;
    }

    img{
    max-width: 100%;
    }

    /* Your css reset here */
    .form-group{

        ${props.attributes.theme.input.toCSS()}
    }

    .input-error{
        ${props.attributes.theme.input.errorFont.toCSS()}
    }

    form.login-form button {
        ${props.attributes.theme.button.toCSS()}
    }

    form.login-form {
        margin: auto;
        max-width: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-top: 10px;
        ${props.attributes.theme.loginForm.toCSS()}
    }

    form.login-form a{
        ${props.attributes.theme.link.toCSS()}
    }

    form.login-form {
        min-width: 250px;
        button{
            display: block;
            width: 100%;
        }
    }

    ul li, nav li{
        list-style: none;
    }

    .form-group.active:not(.cc-input-wrap) label {
        top: -8px;
        left: 8px;
        z-index: 3;
        transform: all;
        padding: 0 4px;
        background: #fbfbff;
    }

    .form-group label:not(.cc-label) {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 10px;
        z-index: 1;
        transition: 0.2s ease;
        user-select: none;
        cursor: text;
    }

    .form-group {
        margin-bottom: 25px;
        position: relative;
        display: flex;
        margin-top: 5px;

        .input-error {
            position: absolute;
            right: 0;
            bottom: -20px;
        }
    }

    button{
        cursor: pointer;
    }

    .MuiDialog-container{
        font-family: "${props.attributes.theme.fontFamily}", sans-serif !important;
    }
`;

function Homepage(props: { attributes: ProductHomepageAttributes; children: JSX.Element }) {
  const { isLoggedIn, isLoginDialogOpen, setLoginDialogOpen, login, logout } =
    useContext(ProductServerAuthContext);
  const config = useContext(DomainConfigContext);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  let location = useLocation();

  // watch route changes
  React.useEffect(() => {
    // hide login on navigation
    setLoginDialogOpen && setLoginDialogOpen(false);
    // scroll to top
    window.scrollTo(0, 0);
  }, [location]);

  // inject selected font
  useEffect(() => {
    var link = document?.querySelector("#font-injection") || document.createElement("link");
    link.setAttribute("rel", "stylesheet");
    link.setAttribute("type", "text/css");
    link.setAttribute("id", "font-injection");
    link.setAttribute(
      "href",
      `https://fonts.googleapis.com/css2?family=${props.attributes.theme.fontFamily}:wght@400;500;600;700&display=swap`
    );
    document.head.appendChild(link);
  }, [props.attributes.theme.fontFamily]);

  useEffect(() => {

    // redirect if inside iframe
    if (window.self !== window.top && window.top) {
      window.top!.location.href = window.location.href;
    }
  }, [])

  const buildChat = (type: ChatType) => {
    switch (type) {
      case ChatType.FreshChat:
        return <FreshChat></FreshChat>;
      case ChatType.ICanChat:
        return <ICanChat></ICanChat>;
      default:
        return <></>;
    }
  };

  const iframe = useContext(IframeContext);

  const cache = createCache({
    key: "css",
    container: iframe?.contentWindow?.document?.head,
    prepend: true,
  });

  const GlobalStyle = buildGlobalStyle({ attributes: props.attributes });

  return (
    <CacheProvider value={cache}>
      <StyleSheetManager target={iframe?.contentWindow?.document?.head}>
        <>
          <GlobalStyle />
          <BaseCSS />
          <HompageTheme {...props}>
            <div className={"Homepage "}>
              {props.children}

              {/* login dialog */}
              {isLoginDialogOpen && <LoginDialogV2></LoginDialogV2>}
            </div>
            {/* build chat */}
            {!isEditor() && buildChat(props.attributes.chat)}
          </HompageTheme>
        </>
      </StyleSheetManager>
    </CacheProvider>
  );
}

ProductHomepageV2.prototype.Props = ProductHomepageAttributes;

export default ProductHomepageV2;
